import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaxonomyNode } from '../../pages/settings/TaxonomySettingsPage';

interface TaxonomyTreeProps {
  nodes: TaxonomyNode[];
  selectedNodeId: string | null;
  onSelectNode: (node: TaxonomyNode) => void;
  onCreateChild?: (parentNode: TaxonomyNode) => void;
}

interface TaxonomyTreeNodeProps {
  node: TaxonomyNode;
  level: number;
  selectedNodeId: string | null;
  onSelectNode: (node: TaxonomyNode) => void;
  onCreateChild?: (parentNode: TaxonomyNode) => void;
}

function TaxonomyTreeNode({
  node,
  level,
  selectedNodeId,
  onSelectNode,
  onCreateChild,
}: TaxonomyTreeNodeProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels

  const hasChildren = node.children && node.children.length > 0;
  const isSelected = node.id === selectedNodeId;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSelect = () => {
    onSelectNode(node);
  };

  const handleCreateChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateChild?.(node);
  };

  // Calculate indentation based on level
  const paddingLeft = `${level * 1.5}rem`;

  return (
    <div>
      <div
        className={`
          flex items-center gap-2 p-2 rounded-lg cursor-pointer transition
          ${
            isSelected
              ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-2 border-transparent'
          }
        `}
        style={{ paddingLeft }}
        onClick={handleSelect}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={handleToggle}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            aria-label={isExpanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
          >
            <span className="text-xs font-bold dark:text-gray-300">
              {isExpanded ? '−' : '+'}
            </span>
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Icon */}
        {node.icon && (
          <span className="flex-shrink-0 text-lg" aria-hidden="true">
            {node.icon}
          </span>
        )}

        {/* Color Indicator */}
        {node.color && (
          <span
            className="flex-shrink-0 w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600"
            style={{ backgroundColor: node.color }}
            aria-hidden="true"
          />
        )}

        {/* Node Name */}
        <span className="flex-1 font-medium text-sm dark:text-white truncate">
          {node.name}
        </span>

        {/* Content Count Badge */}
        {node.linkedContentCount !== undefined && node.linkedContentCount > 0 && (
          <span className="flex-shrink-0 px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
            {node.linkedContentCount}
          </span>
        )}

        {/* Level Badge */}
        <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
          L{level}
        </span>

        {/* Add Child Button (visible on hover for selected node) */}
        {isSelected && onCreateChild && (
          <button
            onClick={handleCreateChild}
            className="flex-shrink-0 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition"
            title={t('settings.taxonomy.addChild', 'Add child category')}
          >
            + {t('settings.taxonomy.child', 'Child')}
          </button>
        )}
      </div>

      {/* Children (recursive) */}
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children!.map((childNode) => (
            <TaxonomyTreeNode
              key={childNode.id}
              node={childNode}
              level={level + 1}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              onCreateChild={onCreateChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TaxonomyTree({
  nodes,
  selectedNodeId,
  onSelectNode,
  onCreateChild,
}: TaxonomyTreeProps) {
  const { t } = useTranslation();

  // The API already returns a tree structure with nested children
  // No need to rebuild the tree
  if (nodes.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        <p>{t('settings.taxonomy.noNodes', 'No taxonomy nodes found')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {nodes.map((node) => (
        <TaxonomyTreeNode
          key={node.id}
          node={node}
          level={0}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          onCreateChild={onCreateChild}
        />
      ))}
    </div>
  );
}
